import { ModalManager } from '@accedo/modal';
import { Nav, NavProvider } from '@accedo/spatial-nav';
import { FunctionalComponent } from 'preact';
import { Suspense, lazy } from 'preact/compat';

import { Route, Router } from 'preact-router';
import { useEffect } from 'preact/hooks';
import ActivationScreen from '##/components/ActivationScreen/ActivationScreen';
import { layout } from '##/components/HomePage/setup';
import { StoreProvider } from '##/services/store/store';
import {
  ErrorBoundary,
  ErrorPage,
  ErrorScreenProvider,
} from '##/components/Error';
import {
  LoadingIndicator,
  setLoadingIndicatorVisibility,
} from '##/components/LoadingIndicator/LoadingIndicator';
import { SearchLanding } from '##/components/Search/SearchLanding';
import { SideNav } from '##/components/SideNav/SideNav';
import KeyEventHelper from '##/utils/KeyEventHelper';
import { addScreenLogger } from '##/utils';
import { PlatformMessagingProvider } from '##/services/platformMessaging/PlatformMessagingContext';
import { PageNavigation } from './utils/PageNavigation';
import { AppContent } from './AppContent';
import { Redirect } from './components/Redirect/Redirect';
import { ShowsIndexLandingPage } from './components/ShowsIndex/ShowsIndexPage';
import { NewsPage } from './components/NewsPage/NewsPage';
import { SettingsPage } from './components/SettingsPage/Settings';
import { LiveTvPage } from './components/LiveTvPage/LiveTvPage';
import VideoUpNext from './components/VideoUpNextPage/VideoUpNext';
import { InactivePage } from './components/VideoUpNextPage/InactivePage/InactivePage';
import { HomePage } from './components/HomePage/HomePage';
import { nav } from './init';
import LoadingPage from './components/LoadingPage';
import TealiumBridge from './services/analytics/Tealium/TealiumBridge';

const LazyPlayer = lazy(() =>
  import('./components/Player/Interface/VideoPlayer'),
);
const LazyShow = lazy(() => import('./components/ShowPage/ShowPage'));

const App: FunctionalComponent = () => {
  useEffect(() => {
    setLoadingIndicatorVisibility?.(false);
    addScreenLogger();

    return () => {
      TealiumBridge.onAppExit();
    };
  }, []);

  KeyEventHelper.init();

  return (
    <StoreProvider>
      <NavProvider nav={nav}>
        <div id="app">
          <Nav.Horizontal id="root">
            <ErrorScreenProvider>
              <ErrorBoundary>
                <PlatformMessagingProvider>
                  <AppContent>
                    <SideNav />
                    <Nav.Vertical id="content">
                      <LoadingIndicator />
                      <Suspense fallback={<></>}>
                        <Router history={PageNavigation.getHistoryForRouter()}>
                          <Route path="/show" component={LazyShow} />
                          <Route path="/show/:showId" component={LazyShow} />
                          <Route
                            path="/show/:showId/:seasonId"
                            component={LazyShow}
                          />
                          <Route
                            path="/shows"
                            component={ShowsIndexLandingPage}
                          />
                          <Route
                            path="/shows/:initialCategory"
                            component={ShowsIndexLandingPage}
                          />
                          <Route
                            path="/inactive-page"
                            component={InactivePage}
                          />
                          <Route path="/up-next" component={VideoUpNext} />
                          <Route path="/live-tv" component={LiveTvPage} />
                          <Route path="/news" component={NewsPage} />
                          <Route path="/search" component={SearchLanding} />
                          <Route path="/sign-in" component={ActivationScreen} />
                          <Route path="/setting" component={SettingsPage} />
                          <Route
                            path="/player/vod/:videoId"
                            component={LazyPlayer}
                          />
                          <Route
                            path="/player/live/:videoId"
                            component={LazyPlayer}
                          />
                          <Redirect path="/sign-out" to="/" />
                          <Route path="/error" component={ErrorPage} />
                          <Route path="/loading" component={LoadingPage} />
                          <Route component={HomePage} layout={layout} default />
                        </Router>
                      </Suspense>
                    </Nav.Vertical>
                  </AppContent>
                </PlatformMessagingProvider>
              </ErrorBoundary>
            </ErrorScreenProvider>
          </Nav.Horizontal>
          <div id="modal" />
          <ModalManager fallBackFocusId="content" rootDomId="modal" />
        </div>
      </NavProvider>
    </StoreProvider>
  );
};

export default App;
